import { useEffect, useRef } from "react"

export interface UseDebouncedEffectOptions {
  /**
   * Callback function to execute after debounce delay.
   * Can optionally return a cleanup function (like useEffect).
   */
  callback: () => void | (() => void)

  /**
   * Debounce delay in milliseconds
   */
  delay: number

  /**
   * Condition function that returns false to skip execution.
   * Checked both before debouncing and before callback execution.
   */
  condition?: () => boolean

  /**
   * Callback executed immediately when condition is true and debouncing starts.
   * Use this for immediate UI feedback (e.g., showing loading state).
   * Note: This executes BEFORE the debounce delay, on every change that passes condition.
   */
  onDebounceStart?: () => void

  /**
   * Callback executed when condition is false (e.g., reset state).
   */
  onSkip?: () => void

  /**
   * Dependency array for the effect.
   * Include values that should trigger the debounced effect (e.g., form values, edit state).
   */
  deps: React.DependencyList
}

/**
 * Effect hook with condition checking and debounce functionality.
 *
 * Key features:
 * - Debounces the callback execution
 * - Checks condition before starting debounce and before executing callback
 * - Supports cleanup functions from callback (like useEffect)
 * - Properly cleans up on unmount and re-runs
 * - Automatically handles callback stability using refs
 *
 * @example
 * ```tsx
 * // Simple inline usage - no need for useCallback!
 * useDebouncedEffect({
 *   callback: () => {
 *     checkSlug({ searchParams: new URLSearchParams({ slug: editedSlug }) })
 *   },
 *   delay: 500,
 *   condition: () => !!editedSlug && editedSlug !== channel.slug && isEditingBasicInfo,
 *   onDebounceStart: () => setIsCheckingSlug(true),
 *   onSkip: () => {
 *     setSlugAvailable(null)
 *     setIsCheckingSlug(false)
 *   },
 *   deps: [editedSlug, channel.slug, isEditingBasicInfo]
 * })
 * ```
 */
export function useDebouncedEffect(options: UseDebouncedEffectOptions): void {
  const { callback, delay, condition, onDebounceStart, onSkip, deps } = options

  // Use refs to store the latest callbacks to avoid recreating the effect
  const callbackRef = useRef(callback)
  const conditionRef = useRef(condition)
  const onDebounceStartRef = useRef(onDebounceStart)
  const onSkipRef = useRef(onSkip)

  // Update refs on every render (this is safe and recommended by React team)
  callbackRef.current = callback
  conditionRef.current = condition
  onDebounceStartRef.current = onDebounceStart
  onSkipRef.current = onSkip

  const timeoutRef = useRef<NodeJS.Timeout>(undefined)
  const cleanupRef = useRef<(() => void) | void>(undefined)

  useEffect(() => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Run cleanup from previous callback execution
    if (cleanupRef.current) {
      cleanupRef.current()
      cleanupRef.current = undefined
    }

    // Check condition
    if (conditionRef.current && !conditionRef.current()) {
      onSkipRef.current?.()
      return
    }

    // Execute onDebounceStart for immediate UI feedback
    onDebounceStartRef.current?.()

    // Set debounce timer
    timeoutRef.current = setTimeout(() => {
      // Re-check condition before executing (state might have changed during delay)
      if (conditionRef.current && !conditionRef.current()) {
        onSkipRef.current?.()
        return
      }

      // Execute callback and store cleanup function if returned
      cleanupRef.current = callbackRef.current()
    }, delay)

    // Cleanup on unmount or re-run
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (cleanupRef.current) {
        cleanupRef.current()
      }
    }
    // Include delay so changing it will restart the debounce
    // Callbacks are tracked via refs and don't need to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, delay])
}
